import {
    Alert02Icon,
    Calendar01Icon,
    Cancel01Icon,
    CheckmarkSquare02Icon,
    ClockIcon,
    FileEmpty01Icon,
    Folder01Icon,
    UnfoldMoreIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Trans, useLingui } from "@lingui/react/macro";
import type * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import { createFormHook, createFormHookContexts, useStore } from "@tanstack/react-form";
import type { Type } from "arktype";
import { format } from "date-fns";
import {
    type ChangeEvent,
    type ComponentProps,
    createContext,
    type KeyboardEventHandler,
    useContext,
    useEffect,
    useId,
    useState,
} from "react";
import type { DayPickerProps, PropsSingle } from "react-day-picker";
import { Badge } from "~/components/ui/badge";
import { ButtonLoading } from "~/components/ui/button-loading";
import { Calendar } from "~/components/ui/calendar";
import { Label } from "~/components/ui/label";
import { RadioGroup } from "~/components/ui/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";
import type { IOptionable } from "~/models/optionable";
import { getMaxLength } from "~/modules/validation/helpers";
import { Button } from "./button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "./command";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { TextEditor } from "./text-editor";

const {
	fieldContext,
	formContext,
	useFieldContext: _useFieldContext,
	useFormContext,
} = createFormHookContexts();

const { useAppForm, withForm } = createFormHook({
	fieldContext,
	formContext,
	fieldComponents: {
		FormCombobox,
		FormConstraints,
		FormControl,
		FormDatePicker,
		FormDateTimePicker,
		FormDescription,
		FormTextEditor,
		FormHelper,
		FormInput,
		FormItem,
		FormLabel,
		FormLabelOptional,
		FormMessage,
		FormRadioGroup,
		FormSelect,
		FormSwitch,
		FormTagsInput,
		FormTagsList,
		FormTextarea,
	},
	formComponents: {
		FormButton,
		FormErrorMessages,
	},
});

type FormItemContextValue = {
	id: string;
	schema?: Type;
};

const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue);

function FormItem({
	asChild,
	className,
	schema,
	...props
}: ComponentProps<"div"> & { asChild?: boolean; schema?: Type }) {
	const id = useId();
	const Comp = asChild ? Slot : "div";

	return (
		<FormItemContext.Provider value={{ id, schema }}>
			<Comp className={cn("grid gap-2", className)} data-slot="form-item" {...props} />
		</FormItemContext.Provider>
	);
}

function useFormField<TValue = string>() {
	const { id, schema } = useContext(FormItemContext);
	const { name, store, state, ...fieldContext } = _useFieldContext<TValue>();

	const errors = useStore(store, (state) => state.meta.errors);
	if (!fieldContext) {
		throw new Error("useFieldContext should be used within <FormItem>");
	}

	return {
		id,
		name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		errors,
		schema,
		state,
		store,
		...fieldContext,
	};
}

function FormLabel({ className, ...props }: ComponentProps<typeof LabelPrimitive.Root>) {
	const { errors, formItemId } = useFormField();

	return (
		<Label
			className={cn("data-[error=true]:text-destructive", className)}
			data-error={!!errors.length}
			data-slot="form-label"
			htmlFor={formItemId}
			{...props}
		/>
	);
}

function FormControl({ ...props }: ComponentProps<typeof Slot>) {
	const { errors, formItemId, formDescriptionId, formMessageId } = useFormField();

	return (
		<Slot
			aria-describedby={
				!errors.length ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`
			}
			aria-invalid={!!errors.length}
			data-slot="form-control"
			id={formItemId}
			{...props}
		/>
	);
}

function FormInput({
	asChild = false,
	...props
}: ComponentProps<typeof Input> & { asChild?: boolean }) {
	const field = useFormField();
	const Comp = asChild ? Slot : Input;

	return (
		<Comp
			onBlur={field.handleBlur}
			onChange={(e) => field.handleChange(e.target.value)}
			value={field.state.value}
			{...props}
		/>
	);
}

function FormRadioGroup({
	asChild = false,
	...props
}: ComponentProps<typeof RadioGroup> & { asChild?: boolean }) {
	const field = useFormField();
	const Comp = asChild ? Slot : RadioGroup;

	return (
		<Comp
			onBlur={field.handleBlur}
			onValueChange={(value) => field.handleChange(value)}
			value={field.state.value}
			{...props}
		/>
	);
}

function FormSwitch({
	asChild = false,
	...props
}: ComponentProps<typeof Switch> & { asChild?: boolean }) {
	const field = useFormField<boolean>();
	const Comp = asChild ? Slot : Switch;

	return (
		<Comp
			onBlur={field.handleBlur}
			onCheckedChange={(checked) => field.handleChange(checked)}
			checked={field.state.value}
			{...props}
		/>
	);
}

function FormTextarea({
	asChild = false,
	...props
}: ComponentProps<typeof Textarea> & { asChild?: boolean }) {
	const field = useFormField();
	const Comp = asChild ? Slot : Textarea;

	return (
		<Comp
			onBlur={field.handleBlur}
			onChange={(e) => field.handleChange(e.target.value)}
			value={field.state.value}
			{...props}
		/>
	);
}

function FormSelect({
	className,
	options,
	placeholder,
	triggerProps,
	...props
}: {
	className?: string;
	options: Array<IOptionable>;
	placeholder?: string;
	triggerProps?: ComponentProps<typeof SelectTrigger>;
} & ComponentProps<typeof Select>) {
	const { t } = useLingui();
	const field = useFormField();

	return (
		<Select
			onOpenChange={(open) => {
				if (!open) {
					field.handleBlur();
				}
			}}
			value={field.state.value}
			onValueChange={(value) => field.handleChange(value)}
			{...props}
		>
			<FormControl>
				<SelectTrigger className={cn(className)} {...triggerProps}>
					<SelectValue placeholder={placeholder ?? t`Select an option`} />
				</SelectTrigger>
			</FormControl>
			<SelectContent className="p-0">
				{options.map((option) => (
					<SelectItem key={option.value} value={option.value}>
						<div className="flex flex-col items-start">
							<div>{option.label}</div>
							{option.description && (
								<span data-desc className="text-muted-foreground mt-1 block text-xs">
									{option.description}
								</span>
							)}
						</div>
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

function FormCombobox({
	align,
	className,
	onSelect,
	options,
	placeholder = `Select an option`,
	emptyPlaceholder = `No option found`,
	searchPlaceholder = `Search an option`,
}: {
	align?: "center" | "end" | "start";
	className?: string;
	emptyPlaceholder?: string;
	onSelect?: (option: IOptionable) => void;
	options: Array<IOptionable>;
	placeholder?: string;
	searchPlaceholder?: string;
}) {
	const field = useFormField<string | Array<string>>();

	// @ts-expect-error Mode is not recognized
	const isMultiple = field.options.mode === "array";

	const selectedOption = isMultiple
		? options.filter(
				(option) => Array.isArray(field.state.value) && field.state.value.includes(option.value),
			)
		: options.find((option) => option.value === field.state.value);

	function isSelected(option: IOptionable) {
		if (isMultiple && Array.isArray(field.state.value)) {
			return field.state.value.includes(option.value);
		}

		return field.state.value === option.value;
	}

	return (
		<Popover>
			<PopoverTrigger asChild>
				<FormControl>
					<Button
						className={cn(
							"max-w-full min-w-0 justify-between",
							!field.state.value && "text-muted-foreground",
							className,
						)}
						role="combobox"
						variant="outline"
					>
						<span className="truncate">
							{isMultiple
								? placeholder
								: field.state.value && field.state.value !== ""
									? Array.isArray(selectedOption)
										? placeholder
										: selectedOption?.label
									: placeholder}
						</span>
						<HugeiconsIcon icon={UnfoldMoreIcon} className="opacity-50" />
					</Button>
				</FormControl>
			</PopoverTrigger>
			<PopoverContent className="p-0" align={align}>
				<Command>
					<CommandInput className="h-9" placeholder={searchPlaceholder} />
					<CommandList>
						<CommandEmpty>{emptyPlaceholder}</CommandEmpty>
						<CommandGroup>
							{options.map((option) => (
								<CommandItem
									key={option.value}
									onSelect={() => {
										if (onSelect) {
											onSelect(option);
										} else if (isMultiple) {
											if (isSelected(option)) {
												field.removeValue(field.state.value.indexOf(option.value));
											} else {
												field.pushValue(option.value);
											}
										} else {
											field.handleChange(option.value.toString());
										}
									}}
									value={option.search ?? option.label}
									className={option.level && option.level > 0 ? "pl-4" : ""}
								>
									{option.level === 0 && <HugeiconsIcon icon={Folder01Icon} />}
									{option.level === 1 && <HugeiconsIcon icon={FileEmpty01Icon} />}
									{option.label}
									<HugeiconsIcon
										icon={CheckmarkSquare02Icon}
										className={cn(
											"text-primary ml-auto",
											isSelected(option) ? "opacity-100" : "opacity-0",
										)}
									/>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}

function FormDatePicker({ className, ...props }: PropsSingle & { className?: string }) {
	const field = useFormField();

	const selectedDate = field.state.value ? new Date(field.state.value) : undefined;

	return (
		<Popover>
			<PopoverTrigger asChild>
				<FormControl>
					<Button
						variant={"outline"}
						className={cn(
							"w-[240px] pl-3 text-left font-normal",
							!field.state.value && "text-muted-foreground",
							className,
						)}
					>
						{field.state.value ? (
							format(field.state.value, "PPP")
						) : (
							<span>
								<Trans>Pick a date</Trans>
							</span>
						)}
						<HugeiconsIcon icon={Calendar01Icon} className="ml-auto h-4 w-4 opacity-50" />
					</Button>
				</FormControl>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<Calendar
					selected={selectedDate}
					onSelect={(_, selectedDay) => {
						field.handleChange(selectedDay.toISOString());
					}}
					{...props}
					mode="single"
				/>
			</PopoverContent>
		</Popover>
	);
}

function FormDateTimePicker({
	className,
	...props
}: Omit<DayPickerProps, "mode"> & { className?: string }) {
	const id = useId();
	const field = useFormField();

	const [isMounted, setIsMounted] = useState(false);

	const currentDate = field.state.value ? new Date(field.state.value) : undefined;
	const initialTime = currentDate
		? currentDate.toTimeString().slice(0, 8) // HH:MM:SS
		: "12:00:00";

	const [selectedDate, setSelectedDate] = useState<Date | undefined>(currentDate);
	const [selectedTime, setSelectedTime] = useState(initialTime);

	useEffect(() => {
		if (!isMounted) {
			return;
		}

		if (!selectedDate) {
			field.handleChange("");
			return;
		}

		const [hours, minutes, seconds] = selectedTime.split(":").map(Number);
		const fullDate = new Date(selectedDate);
		fullDate.setHours(hours ?? 0, minutes ?? 0, seconds ?? 0);

		field.handleChange(fullDate.toISOString());
	}, [selectedDate, selectedTime]);

	useEffect(() => {
		setIsMounted(true);
	}, []);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<FormControl>
					<Button
						variant={"outline"}
						className={cn(
							"w-[240px] pl-3 text-left font-normal",
							className,
							!field.state.value && "text-muted-foreground",
						)}
					>
						{field.state.value ? (
							format(field.state.value, "PPP p")
						) : (
							<span>
								<Trans>Pick a date</Trans>
							</span>
						)}
						<HugeiconsIcon icon={Calendar01Icon} className="ml-auto h-4 w-4 opacity-50" />
					</Button>
				</FormControl>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<Calendar
					selected={selectedDate}
					onSelect={(day) => {
						setSelectedDate(day);
					}}
					{...props}
					mode="single"
				/>
				<div className="space-y-3 border-t p-3">
					<div className="flex items-center gap-3">
						<Label htmlFor={id} className="text-xs">
							<Trans>Enter time</Trans>
						</Label>
						<div className="relative grow">
							<Input
								id={id}
								type="time"
								step="1"
								value={selectedTime}
								onChange={(e) => setSelectedTime(e.target.value)}
								className="peer flex-1 appearance-none ps-9 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
							/>
							<div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
								<HugeiconsIcon icon={ClockIcon} aria-hidden="true" className="size-4" />
							</div>
						</div>
					</div>
					<div>
						<Button variant="outline" onClick={() => setSelectedDate(undefined)} className="w-full">
							<Trans>Clear selection</Trans>
						</Button>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}

function FormTextEditor({ ...props }: ComponentProps<typeof TextEditor>) {
	const field = useFormField();

	return (
		<TextEditor
			{...props}
			value={field.state.value}
			onBlur={() => field.handleBlur()}
			onChangeValue={(value) => field.handleChange(value)}
		/>
	);
}

function FormTagsInput({
	asChild = false,
	...props
}: ComponentProps<typeof Input> & { asChild?: boolean }) {
	const field = useFormField<Array<string>>();
	const Comp = asChild ? Slot : Input;

	const [value, setValue] = useState("");

	const addTag = (tagText: string) => {
		const trimmedTag = tagText.trim();
		if (trimmedTag && !field.state.value.includes(trimmedTag)) {
			field.pushValue(trimmedTag);
		}

		setValue("");
	};

	const removeTag = (index: number) => {
		field.removeValue(index);
	};

	const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
		if (e.key === "," || e.key === " " || e.key === "Enter") {
			e.preventDefault();
			addTag(value);
		} else if (e.key === "Backspace" && value === "" && field.state.value.length > 0) {
			removeTag(field.state.value.length - 1);
		}
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;

		if (newValue.includes(",") || newValue.includes(" ")) {
			const parts = newValue.split(/[, ]+/);
			const completeTags = parts.slice(0, -1);
			const remainingInput = parts[parts.length - 1];

			completeTags.forEach((tag) => {
				const trimmedTag = tag.trim();
				if (trimmedTag && !field.state.value.includes(trimmedTag)) {
					field.pushValue(trimmedTag);
				}
			});

			setValue(remainingInput ?? "");
		} else {
			setValue(newValue);
		}
	};

	return (
		<Comp
			onBlur={field.handleBlur}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			value={value}
			{...props}
		/>
	);
}

function FormTagsList() {
	const field = useFormField<Array<string>>();

	const removeTag = (index: number) => {
		field.removeValue(index);
	};

	if (field.state.value.length === 0) {
		return null;
	}

	return (
		<div className="mt-2 flex flex-wrap gap-1">
			{field.state.value.map((tag, index) => (
				<Badge key={tag} color="sky" className="px-2 py-0.5">
					{tag}
					<button
						type="button"
						onClick={() => removeTag(index)}
						className="focus-visible:border-ring focus-visible:ring-ring/50 text-foreground/60 hover:text-foreground shrink-0 cursor-pointer items-center justify-center rounded-[inherit] p-0 transition-[color,box-shadow] outline-none focus-visible:ring-[3px]"
					>
						<HugeiconsIcon icon={Cancel01Icon} className="size-3" />
					</button>
				</Badge>
			))}
		</div>
	);
}

function FormDescription({ className, ...props }: ComponentProps<"p">) {
	const { formDescriptionId } = useFormField();

	return (
		<p
			className={cn("text-muted-foreground text-sm", className)}
			data-slot="form-description"
			id={formDescriptionId}
			{...props}
		/>
	);
}

/**
 * Shows an "— optional" label if the field is optional
 */
function FormLabelOptional({
	className,
	children,
	optional,
	...props
}: ComponentProps<"span"> & { optional?: boolean }) {
	if (optional === true) {
		return (
			<span className={cn("text-muted-foreground italic", className)} {...props}>
				{children ?? (
					<>
						{" "}
						— <Trans>optional</Trans>
					</>
				)}
			</span>
		);
	}

	return null;
}

/**
 * Shows the form constraints helpers.
 * Currently supports "maxLength" constraints
 *
 * @example "0 / 200 characters"
 */
function FormConstraints({
	className,
	schema,
	...props
}: ComponentProps<typeof FormHelper> & { schema: Type }) {
	const { state } = useFormField();

	if (!schema) return null;

	const maxLength = getMaxLength(schema);
	if (maxLength !== null) {
		return (
			<FormHelper className={cn("text-right italic", className)} {...props}>
				<Trans>
					{{ count: state.value?.length ?? 0 }} / {{ maxLength }} characters
				</Trans>
			</FormHelper>
		);
	}

	return null;
}

export function FormHelper({ className, ...props }: ComponentProps<"p">) {
	return (
		<p
			className={cn("text-muted-foreground text-xs", className)}
			data-slot="form-helper"
			{...props}
		/>
	);
}

function FormMessage({ className, ...props }: ComponentProps<"p">) {
	const { errors, formMessageId } = useFormField();
	const body = errors.length ? String(errors.at(0)?.message ?? "") : props.children;

	if (!body) {
		return null;
	}

	return (
		<p
			id={formMessageId}
			className={cn("text-destructive flex items-center gap-2 text-sm", className)}
			data-slot="form-message"
			{...props}
		>
			<HugeiconsIcon icon={Alert02Icon} className="size-4" /> {body}
		</p>
	);
}

function FormErrorMessages({ className, ...props }: ComponentProps<"div">) {
	const { store } = useFormContext();
	const formErrors = useStore(store, (formState) => formState.errors);

	const globalFormErrors = formErrors
		.filter((error) => typeof error === "string" || Array.isArray(error))
		.flat();

	if (globalFormErrors.length <= 0) {
		return null;
	}

	return (
		<div
			className={cn(
				"relative w-full rounded-lg border px-4 py-3 text-sm font-semibold whitespace-pre-line",
				"text-destructive bg-destructive/10 border-destructive/10",
				className,
			)}
			{...props}
		>
			<p>{globalFormErrors.join("\n")}</p>
		</div>
	);
}

export function FormButton({ ...props }: ComponentProps<typeof Button>) {
	const form = useFormContext();

	return (
		<form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
			{([canSubmit, isSubmitting]) => (
				<ButtonLoading
					disabled={props.disabled || isSubmitting || !canSubmit}
					isLoading={isSubmitting}
					type="submit"
					{...props}
				/>
			)}
		</form.Subscribe>
	);
}

export { useAppForm, useFormField, useFormContext, withForm };
